import { useEffect, useState } from "react";
import api from "@/lib/api";
import { UserCog, Ban, RotateCcw, Search, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Pager from "@/components/Pager";
import { downloadCsv } from "@/lib/csv";
import { cad } from "@/lib/pricing";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const STATUS = { active: ["Active", "pf-chip-mint"], suspended: ["Suspended", ""], pending: ["Pending approval", "pf-chip-lav"] };

function SuspendDialog({ merchant, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/merchants/${merchant.id}/suspend`, { reason: reason.trim() });
      toast.success(`${merchant.email} suspended`);
      setReason("");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Only super admins can suspend");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!merchant} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="suspend-modal">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Suspend {merchant?.name}</DialogTitle>
            <DialogDescription>All approved campaigns are paused and the merchant's active sessions end. The action is audit-logged.</DialogDescription>
          </DialogHeader>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Reason (required)
            <textarea required minLength={3} rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              className="pf-input w-full px-3 py-2 mt-1 text-sm normal-case tracking-normal font-normal" placeholder="e.g. fraudulent chargeback pattern" data-testid="suspend-reason" />
          </label>
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} disabled={busy} className="pf-btn-glass px-4 py-2 rounded-full text-sm" data-testid="suspend-cancel">Cancel</button>
            <button type="submit" disabled={busy || reason.trim().length < 3} className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors inline-flex items-center gap-2 disabled:opacity-50" data-testid="suspend-confirm">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{busy ? "Suspending…" : "Suspend"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminMerchants() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1, page_size: 25 });
  const [suspending, setSuspending] = useState(null);
  const [reinstating, setReinstating] = useState(null);
  const { user } = useAuth();
  const canManage = ["admin", "super_admin"].includes(user?.role);  // Support/Operations/Finance: read-only here

  const load = (p = page, query = q) => {
    const params = new URLSearchParams({ page: p, page_size: 25 });
    if (query) params.set("q", query);
    api.get(`/admin/merchants?${params}`).then((r) => {
      setItems(r.data.items || []);
      setMeta({ total: r.data.total, total_pages: r.data.total_pages, page_size: r.data.page_size });
      setPage(r.data.page);
    });
  };
  useEffect(() => { load(1, ""); /* eslint-disable-next-line */ }, []);

  const reinstate = async (m) => {
    setReinstating(m.id);
    try {
      await api.post(`/admin/merchants/${m.id}/reinstate`);
      toast.success(`${m.email} reinstated`);
      load(page, q);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reinstate failed");
    } finally {
      setReinstating(null);
    }
  };

  return (
    <div data-testid="admin-merchants">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="pf-chip pf-chip-cyan"><UserCog className="w-3 h-3" /> Merchant management</div>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Merchants</h2>
        </div>
        {canManage && <button onClick={() => downloadCsv(`/admin/merchants.csv${q ? `?q=${encodeURIComponent(q)}` : ""}`, "cyannect-merchants.csv")} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="merchant-export-csv">
          <Download className="w-3 h-3" /> Download CSV
        </button>}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(1, q); }} className="mt-6 flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" aria-label="Search merchants" className="pf-input w-full pl-9 pr-3 py-2 text-sm" data-testid="merchant-search-input" />
        </div>
        <button type="submit" className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold" data-testid="merchant-search-btn">Search</button>
      </form>

      <div className="pf-card overflow-x-auto mt-6">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-5 py-3">Merchant</th>
              <th className="px-5 py-3">Store</th>
              <th className="px-5 py-3">Offers</th>
              <th className="px-5 py-3">Redemptions</th>
              <th className="px-5 py-3">GMV</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => {
              const [label, chip] = STATUS[m.status] || [m.status, "pf-chip-lav"];
              return (
                <tr key={m.id} className="border-t border-white/60" data-testid={`merchant-row-${m.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{m.name}</div>
                    <div className="text-xs text-slate-500">{m.email}</div>
                  </td>
                  <td className="px-5 py-3 text-slate-700">{m.store?.name || <span className="text-slate-400 italic">no store</span>}</td>
                  <td className="px-5 py-3 font-mono">{m.active_campaigns}/{m.campaigns_count}</td>
                  <td className="px-5 py-3 font-mono">{m.total_redemptions}</td>
                  <td className="px-5 py-3 font-mono">{cad(m.gmv || 0)}</td>
                  <td className="px-5 py-3"><span className={`pf-chip ${chip}`} data-testid={`merchant-status-${m.id}`}>{label}</span></td>
                  <td className="px-5 py-3 text-right">
                    {!canManage ? null : m.status === "suspended" ? (
                      <button onClick={() => reinstate(m)} disabled={reinstating === m.id} className="pf-btn-glass px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 text-emerald-700 disabled:opacity-50" data-testid={`reinstate-${m.id}`}>
                        <RotateCcw className="w-3 h-3" /> {reinstating === m.id ? "Reinstating…" : "Reinstate"}
                      </button>
                    ) : (
                      <button onClick={() => setSuspending(m)} className="pf-btn-glass px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 text-red-700" data-testid={`suspend-${m.id}`}>
                        <Ban className="w-3 h-3" /> Suspend
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500">No merchants match.</td></tr>}
          </tbody>
        </table>
      </div>

      <Pager page={page} totalPages={meta.total_pages} total={meta.total} pageSize={meta.page_size} onPage={(p) => load(p, q)} testidPrefix="merch-pager" />
      <SuspendDialog merchant={suspending} onClose={() => setSuspending(null)} onDone={() => { setSuspending(null); load(page, q); }} />
    </div>
  );
}
